import { useState } from 'react';
import { GlassCard, GlassCardHeader, GlassCardTitle, GlassCardContent } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api';
import {
  Zap,
  RotateCw,
  Trash2,
  Database,
  Server,
  CheckCircle2,
  AlertCircle,
  HardDrive,
  Network,
  Video,
  Bot,
  Activity,
} from 'lucide-react';

type ActionGroup = 'app' | 'db' | 'video' | 'cache';
type ActionVariant = 'default' | 'warning' | 'danger';
type ActionResult = 'success' | 'error';

interface QuickAction {
  id: string;
  group: ActionGroup;
  node: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  variant: ActionVariant;
  method: 'GET' | 'POST';
  endpoint: string;
  body?: Record<string, unknown>;
  requires2fa?: boolean;
}

const GROUP_LABELS: Record<ActionGroup, string> = {
  app: 'Tier App (FE / BE-1 / BE-2)',
  db: 'Tier DB (DB-1 / DB-2)',
  video: 'Tier Video (BE-3 / BE-4)',
  cache: 'Tier Cache (Redis)',
};

const QUICK_ACTIONS: QuickAction[] = [
  // ── Tier App ───────────────────────────────────────────────────────────────
  {
    id: 'reload-fe1-nginx',
    group: 'app',
    node: 'FE-1',
    label: 'Reload nginx FE-1',
    description: 'Graceful reload (no downtime) del reverse proxy',
    icon: <Network className="h-4 w-4" />,
    variant: 'default',
    method: 'POST',
    endpoint: '/master/security/system/service-action',
    body: { service: 'nginx', action: 'reload', server: 'fe1' },
    requires2fa: true,
  },
  {
    id: 'restart-be1',
    group: 'app',
    node: 'BE-1',
    label: 'Restart BE-1',
    description: 'Riavvia Node.js + FreeSWITCH (primary)',
    icon: <Server className="h-4 w-4" />,
    variant: 'default',
    method: 'POST',
    endpoint: '/master/security/system/service-action',
    body: { service: 'voip-backend', action: 'restart', server: 'be1' },
  },
  {
    id: 'restart-be2',
    group: 'app',
    node: 'BE-2',
    label: 'Restart BE-2',
    description: 'Riavvia Node.js API only',
    icon: <Server className="h-4 w-4" />,
    variant: 'default',
    method: 'POST',
    endpoint: '/master/security/system/service-action',
    body: { service: 'voip-backend', action: 'restart', server: 'be2' },
  },

  // ── Tier DB ────────────────────────────────────────────────────────────────
  {
    id: 'vacuum-db1',
    group: 'db',
    node: 'DB-1',
    label: 'Vacuum DB-1',
    description: 'Ottimizza PostgreSQL leader',
    icon: <Database className="h-4 w-4" />,
    variant: 'warning',
    method: 'POST',
    endpoint: '/master/security/system/vacuum-db',
  },
  {
    id: 'replica-status-db2',
    group: 'db',
    node: 'DB-2',
    label: 'Stato replica DB-2',
    description: 'Patroni REST: ruolo, stato, lag bytes',
    icon: <Activity className="h-4 w-4" />,
    variant: 'default',
    method: 'POST',
    endpoint: '/master/cluster-ops/db/db2/replica-status',
    requires2fa: true,
  },
  {
    id: 'haproxy-reload-db2',
    group: 'db',
    node: 'DB-2',
    label: 'Reload HAProxy DB-2',
    description: 'Reload locale routing :5432/:5433',
    icon: <HardDrive className="h-4 w-4" />,
    variant: 'warning',
    method: 'POST',
    endpoint: '/master/cluster-ops/db/db2/haproxy/reload',
    requires2fa: true,
  },

  // ── Tier Video ─────────────────────────────────────────────────────────────
  {
    id: 'restart-livekit-be3',
    group: 'video',
    node: 'BE-3',
    label: 'Restart LiveKit BE-3',
    description: 'Riavvia livekit-server SFU',
    icon: <Video className="h-4 w-4" />,
    variant: 'warning',
    method: 'POST',
    endpoint: '/master/cluster-ops/video/be3/livekit-server/restart',
    requires2fa: true,
  },
  {
    id: 'restart-agent-be3',
    group: 'video',
    node: 'BE-3',
    label: 'Restart Agent BE-3',
    description: 'Riavvia livekit-agent-worker (trascrizione)',
    icon: <Bot className="h-4 w-4" />,
    variant: 'default',
    method: 'POST',
    endpoint: '/master/cluster-ops/video/be3/livekit-agent-worker/restart',
    requires2fa: true,
  },
  {
    id: 'restart-livekit-be4',
    group: 'video',
    node: 'BE-4',
    label: 'Restart LiveKit BE-4',
    description: 'Riavvia livekit-server SFU',
    icon: <Video className="h-4 w-4" />,
    variant: 'warning',
    method: 'POST',
    endpoint: '/master/cluster-ops/video/be4/livekit-server/restart',
    requires2fa: true,
  },
  {
    id: 'restart-agent-be4',
    group: 'video',
    node: 'BE-4',
    label: 'Restart Agent BE-4',
    description: 'Riavvia livekit-agent-worker (trascrizione)',
    icon: <Bot className="h-4 w-4" />,
    variant: 'default',
    method: 'POST',
    endpoint: '/master/cluster-ops/video/be4/livekit-agent-worker/restart',
    requires2fa: true,
  },

  // ── Tier Cache ─────────────────────────────────────────────────────────────
  {
    id: 'flush-cache',
    group: 'cache',
    node: 'REDIS-1',
    label: 'Flush Cache',
    description: 'Svuota tutte le cache Redis',
    icon: <Trash2 className="h-4 w-4" />,
    variant: 'warning',
    method: 'POST',
    endpoint: '/master/security/system/flush-cache',
  },
];

interface ApiErrorShape {
  code?: string;
  message?: string;
}

function extractErrorMessage(payload: { error?: unknown; message?: string }): string | null {
  if (typeof payload.error === 'string') return payload.error;
  if (typeof payload.error === 'object' && payload.error !== null) {
    const e = payload.error as ApiErrorShape;
    if (typeof e.message === 'string') return e.message;
  }
  if (typeof payload.message === 'string') return payload.message;
  return null;
}

export function QuickActionsWidget() {
  const { toast } = useToast();
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, ActionResult>>({});

  const executeAction = async (action: QuickAction) => {
    setLoadingAction(action.id);
    setResults((prev) => {
      const next = { ...prev };
      delete next[action.id];
      return next;
    });

    try {
      let totpCode: string | undefined;
      if (action.requires2fa) {
        const input = window.prompt(`Codice 2FA TOTP per "${action.label}"`);
        if (!input) {
          setLoadingAction(null);
          return;
        }
        totpCode = input.trim();
      }

      const body = { ...(action.body || {}), ...(totpCode ? { totpCode } : {}) };
      const data =
        action.method === 'GET'
          ? await apiClient.get(action.endpoint)
          : await apiClient.post(action.endpoint, body);

      if (data.success) {
        setResults((prev) => ({ ...prev, [action.id]: 'success' }));
        toast({
          title: 'Azione completata',
          description: `${action.label} (${action.node}) eseguito con successo`,
        });
      } else {
        const errMsg = extractErrorMessage(data) || 'Azione fallita';
        throw new Error(errMsg);
      }
    } catch (error: unknown) {
      setResults((prev) => ({ ...prev, [action.id]: 'error' }));
      const errorMessage = error instanceof Error ? error.message : 'Operazione fallita';
      toast({
        title: 'Errore',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoadingAction(null);
      setTimeout(() => {
        setResults((prev) => {
          const updated = { ...prev };
          delete updated[action.id];
          return updated;
        });
      }, 3000);
    }
  };

  const getButtonClasses = (action: QuickAction) => {
    const baseClasses = 'w-full justify-start gap-3 h-auto py-3 px-4 transition-all';
    if (action.variant === 'danger') {
      return `${baseClasses} border-rose-300 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-500/10`;
    }
    if (action.variant === 'warning') {
      return `${baseClasses} border-amber-300 dark:border-amber-500/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/10`;
    }
    return `${baseClasses} border-border dark:border-white/20 text-foreground dark:text-foreground hover:bg-muted`;
  };

  const groups: ActionGroup[] = ['app', 'db', 'video', 'cache'];

  return (
    <GlassCard variant="elevated" className="h-full">
      <GlassCardHeader className="p-4 pb-2">
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-400" />
          <GlassCardTitle className="text-base">Quick Actions · cluster 8 nodi</GlassCardTitle>
        </div>
      </GlassCardHeader>
      <GlassCardContent className="p-4 pt-2">
        <div className="space-y-4">
          {groups.map((group) => {
            const actionsInGroup = QUICK_ACTIONS.filter((a) => a.group === group);
            if (actionsInGroup.length === 0) return null;
            return (
              <div key={group}>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground dark:text-muted-foreground/70 font-semibold mb-1.5 px-1">
                  {GROUP_LABELS[group]}
                </div>
                <div className="space-y-2">
                  {actionsInGroup.map((action) => (
                    <Button
                      key={action.id}
                      variant="outline"
                      onClick={() => executeAction(action)}
                      disabled={loadingAction !== null}
                      className={getButtonClasses(action)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {loadingAction === action.id ? (
                          <RotateCw className="h-4 w-4 animate-spin" />
                        ) : results[action.id] === 'success' ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                        ) : results[action.id] === 'error' ? (
                          <AlertCircle className="h-4 w-4 text-rose-400" />
                        ) : (
                          action.icon
                        )}
                        <div className="text-left">
                          <div className="font-medium text-sm">
                            {action.label}
                            {action.requires2fa && (
                              <span className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-500/80">2FA</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                            {action.description}
                          </div>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-3 border-t border-border dark:border-white/10">
          <p className="text-xs text-muted-foreground dark:text-muted-foreground text-center">
            FE-1 · BE-1 · BE-2 · DB-1 · DB-2 · BE-3 · BE-4 · REDIS-1
          </p>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}
